/**
 * AFC Delivery Countdown — always-ticking announcement bar timer.
 *
 * Fetches live delivery config from the aiflorist worker API and ticks
 * every second, showing a countdown to the next delivery cutoff.
 *
 * All time calculations use Asia/Singapore (SGT).
 *
 * Display states:
 *   same-day, > 3h  → "🚚 Xh Xm Xs — order by HH:MM for same-day [slot] delivery"
 *   same-day, ≤ 3h  → "⏰ Xh Xm Xs left for same-day delivery"
 *   next-day/future  → "🚚 Xh Xm Xs — order by HH:MM for [day] [slot] delivery"
 */
(function () {
  'use strict';

  var CACHE_KEY = 'afc_dc_config';
  var CACHE_TTL = 30 * 60 * 1000;
  var URGENT_MS = 3 * 60 * 60 * 1000;
  var DAY_MS = 24 * 60 * 60 * 1000;
  var API_BASE = 'https://agent.aiflorist.studio';
  var MOBILE_HOME_BREAKPOINT = '(max-width: 749px)';
  var MOBILE_HOME_SCROLL_THRESHOLD = 24;

  var SGT = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });

  function sgtParts() {
    var parts = SGT.formatToParts(new Date());
    var o = {};
    for (var i = 0; i < parts.length; i++) {
      o[parts[i].type] = Number(parts[i].value) || 0;
    }
    return o;
  }

  function sgtDateStr() {
    var p = sgtParts();
    return String(p.year).padStart(4, '0') + '-' +
           String(p.month).padStart(2, '0') + '-' +
           String(p.day).padStart(2, '0');
  }

  function sgtMsSinceMidnight() {
    var p = sgtParts();
    return ((p.hour * 60 + p.minute) * 60 + p.second) * 1000;
  }

  function dow(dateStr) {
    return new Date(dateStr + 'T12:00:00').getDay();
  }

  function addDays(dateStr, n) {
    var d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function cutoffMs(slot) {
    return ((slot.cutH || 0) * 60 + (slot.cutM || 0)) * 60 * 1000;
  }

  function fmtTime(h, m) {
    var hr = h % 12 || 12;
    var ap = h < 12 ? 'am' : 'pm';
    return m === 0 ? hr + ap : hr + ':' + String(m).padStart(2, '0') + ap;
  }

  function fmtCountdown(ms) {
    if (ms <= 0) return '0s';
    var t = Math.floor(ms / 1000);
    var h = Math.floor(t / 3600);
    var m = Math.floor((t % 3600) / 60);
    var s = t % 60;
    if (h > 0) return h + 'h ' + m + 'm ' + s + 's';
    if (m > 0) return m + 'm ' + s + 's';
    return s + 's';
  }

  function isBlackout(cfg, d) {
    var list = cfg.blackoutDates || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].date === d) return true;
    }
    return false;
  }

  function allSlotsForDate(cfg, dateStr) {
    if (isBlackout(cfg, dateStr)) return [];
    var d = dow(dateStr);
    return (cfg.timeslots || []).filter(function (s) {
      return s.days && s.days.indexOf(d) >= 0;
    });
  }

  function slotsBeforeCutoff(cfg, dateStr) {
    var slots = allSlotsForDate(cfg, dateStr);
    var nowMs = sgtMsSinceMidnight();
    return slots.filter(function (s) { return nowMs < cutoffMs(s); });
  }

  function computeState(cfg) {
    var today = sgtDateStr();
    var nowMs = sgtMsSinceMidnight();

    var todayOpen = slotsBeforeCutoff(cfg, today);
    if (todayOpen.length > 0) {
      var slot = todayOpen[0];
      var rem = cutoffMs(slot) - nowMs;
      return {
        remaining: rem,
        urgent: rem <= URGENT_MS,
        cutoff: fmtTime(slot.cutH || 0, slot.cutM || 0),
        window: slot.value || '',
        openSlots: todayOpen.length,
        dayLabel: 'same-day',
      };
    }

    for (var i = 1; i <= 7; i++) {
      var futureDate = addDays(today, i);
      var futureSlots = allSlotsForDate(cfg, futureDate);
      if (futureSlots.length > 0) {
        var fSlot = futureSlots[0];
        var msToMidnight = DAY_MS - nowMs;
        var fullDaysBetween = (i - 1) * DAY_MS;
        var rem = msToMidnight + fullDaysBetween + cutoffMs(fSlot);
        var label = i === 1 ? 'tomorrow' : new Date(futureDate + 'T12:00:00')
          .toLocaleDateString('en-SG', { weekday: 'long', timeZone: 'Asia/Singapore' });
        return {
          remaining: rem,
          urgent: false,
          cutoff: fmtTime(fSlot.cutH || 0, fSlot.cutM || 0),
          window: fSlot.value || '',
          openSlots: futureSlots.length,
          dayLabel: label,
        };
      }
    }

    return null;
  }

  var ICON_TRUCK = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>';
  var ICON_CLOCK = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"></circle><path d="M12 9v4l2 2"></path><path d="M5 3 2 6"></path><path d="m22 6-3-3"></path><path d="M6.38 18.7 4 21"></path><path d="M17.64 18.67 20 21"></path></svg>';

  function emitHeightSync() {
    document.dispatchEvent(new CustomEvent('afc:delivery-countdown:sync-height'));
  }

  function setVisibility(el, visible) {
    var nextState = visible ? 'visible' : 'hidden';
    if (el.getAttribute('data-scroll-state') === nextState) return;
    el.setAttribute('data-scroll-state', nextState);
    if (visible) {
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('aria-hidden', 'true');
    }
    emitHeightSync();
  }

  function bindMobileHomeScrollReveal(el) {
    if (el.getAttribute('data-mobile-home-scroll') !== 'true') {
      emitHeightSync();
      return;
    }

    var media = window.matchMedia ? window.matchMedia(MOBILE_HOME_BREAKPOINT) : null;
    var ticking = false;

    function updateVisibility() {
      ticking = false;

      var isMobile = media ? media.matches : window.innerWidth <= 749;
      if (!isMobile) {
        setVisibility(el, true);
        return;
      }

      setVisibility(el, window.scrollY > MOBILE_HOME_SCROLL_THRESHOLD);
    }

    function queueUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    }

    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate);

    if (media) {
      if (typeof media.addEventListener === 'function') {
        media.addEventListener('change', queueUpdate);
      } else if (typeof media.addListener === 'function') {
        media.addListener(queueUpdate);
      }
    }

    updateVisibility();
  }

  function render(el, state) {
    var live = el.querySelector('.afc-dc__live');
    var stat = el.querySelector('.afc-dc__static');
    if (!live || !stat) return;

    stat.style.display = 'none';
    live.style.display = '';

    if (!state) {
      live.innerHTML = '<span class="afc-dc__icon">' + ICON_TRUCK + '</span> ' +
        'Same-day flower delivery across Singapore \u2014 free islandwide';
      return;
    }

    var icon = state.urgent ? ICON_CLOCK : ICON_TRUCK;
    var time = '<strong>' + fmtCountdown(state.remaining) + '</strong>';

    if (state.dayLabel === 'same-day' && state.urgent) {
      live.innerHTML = '<span class="afc-dc__icon">' + icon + '</span> ' +
        time + ' left for same-day delivery' +
        (state.openSlots > 1 ? ' \u2014 ' + state.openSlots + ' slots still open' : '');
    } else {
      live.innerHTML = '<span class="afc-dc__icon">' + icon + '</span> ' +
        time + ' \u2014 order by <strong>' + state.cutoff + '</strong> for ' +
        state.dayLabel + ' ' + state.window + ' delivery';
    }
  }

  function loadConfig(cb) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (c._ts && Date.now() - c._ts < CACHE_TTL) return cb(c.data);
      }
    } catch (e) { /* ignore */ }

    fetch(API_BASE + '/delivery/public?_t=' + Date.now(), {
      credentials: 'omit', cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (payload) {
        if (!payload || !payload.settings) return;
        var data = payload.settings;
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data: data }));
        } catch (e) { /* quota */ }
        cb(data);
      })
      .catch(function () { /* keep static fallback */ });
  }

  function init() {
    var el = document.getElementById('afc-delivery-countdown');
    if (!el) return;

    bindMobileHomeScrollReveal(el);

    loadConfig(function (cfg) {
      function tick() {
        var state = computeState(cfg);
        render(el, state);
      }
      tick();
      setInterval(tick, 1000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
